import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  CostInsightsApi,
  Alert,
  ProductInsightsOptions,
} from '@backstage-community/plugin-cost-insights';
import type {
  Cost,
  Entity,
  Group,
  MetricData,
  Project,
} from '@backstage-community/plugin-cost-insights-common';

/**
 * Cost Insights client that fetches real GCP billing data
 * from the cost-insights-backend plugin (which queries BigQuery).
 */
export class GcpCostInsightsClient implements CostInsightsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('cost-insights');
  }

  private async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    const base = await this.baseUrl();
    const url = new URL(`${base}${path}`);
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined) url.searchParams.set(k, v);
      });
    }
    const response = await this.fetchApi.fetch(url.toString());
    if (!response.ok) {
      throw new Error(
        `Cost Insights API error: ${response.status} ${response.statusText}`,
      );
    }
    return response.json();
  }

  async getLastCompleteBillingDate(): Promise<string> {
    const { date } = await this.get<{ date: string }>(
      '/last-complete-billing-date',
    );
    return date;
  }

  async getUserGroups(_userId: string): Promise<Group[]> {
    return this.get<Group[]>('/groups');
  }

  async getGroupProjects(group: string): Promise<Project[]> {
    return this.get<Project[]>('/group-projects', { group });
  }

  async getGroupDailyCost(group: string, intervals: string): Promise<Cost> {
    return this.get<Cost>('/group-daily-cost', { group, intervals });
  }

  async getProjectDailyCost(
    project: string,
    intervals: string,
  ): Promise<Cost> {
    return this.get<Cost>('/project-daily-cost', { project, intervals });
  }

  async getProductInsights(options: ProductInsightsOptions): Promise<Entity> {
    return this.get<Entity>('/product-insights', {
      product: options.product,
      group: options.group,
      intervals: options.intervals,
      ...(options.project ? { project: options.project } : {}),
    });
  }

  async getDailyMetricData(
    metric: string,
    intervals: string,
  ): Promise<MetricData> {
    return this.get<MetricData>('/daily-metric-data', { metric, intervals });
  }

  async getAlerts(group: string): Promise<Alert[]> {
    return this.get<Alert[]>('/alerts', { group });
  }
}
